type Subscriber<T> = {
  onData: (data: T) => void;
  onError?: (err: Error) => void;
};

type Entry<T> = {
  unsubscribe: () => void;
  subscribers: Set<Subscriber<T>>;
  lastData: T | undefined;
  hasData: boolean;
};

const registry = new Map<string, Entry<unknown>>();

/**
 * Ref-counted shared Firestore listener.
 *
 * Multiple hook instances calling `subscribeShared` with the same `key`
 * share a single underlying `onSnapshot`. The listener mounts on the first
 * subscriber and tears down when the last unsubscribes. Late subscribers
 * receive the most recent snapshot immediately so they don't wait for the
 * next document change.
 *
 * Use this when the same Firestore query is consumed by several mounted
 * components (header bell, side nav, page content) on the same screen.
 */
export function subscribeShared<T>(
  key: string,
  createListener: (
    onData: (data: T) => void,
    onError: (err: Error) => void,
  ) => () => void,
  subscriber: Subscriber<T>,
): () => void {
  let entry = registry.get(key) as Entry<T> | undefined;
  if (!entry) {
    const newEntry: Entry<T> = {
      subscribers: new Set<Subscriber<T>>(),
      lastData: undefined,
      hasData: false,
      unsubscribe: () => {},
    };
    registry.set(key, newEntry as Entry<unknown>);
    entry = newEntry;
    entry.unsubscribe = createListener(
      (data) => {
        entry!.lastData = data;
        entry!.hasData = true;
        entry!.subscribers.forEach((s) => s.onData(data));
      },
      (err) => {
        entry!.subscribers.forEach((s) => s.onError?.(err));
      },
    );
  }
  entry.subscribers.add(subscriber);
  if (entry.hasData) subscriber.onData(entry.lastData as T);

  return () => {
    entry!.subscribers.delete(subscriber);
    if (entry!.subscribers.size === 0) {
      entry!.unsubscribe();
      registry.delete(key);
    }
  };
}
